import React, { useEffect, useRef } from 'react';
import { X, Check, Loader2, RotateCcw, AlertCircle } from 'lucide-react';

const BAR_COUNT = 200;
const MIN_BAR_HEIGHT = 2;
const MAX_BAR_HEIGHT = 56;

const VoiceInputRecording = ({
    audioLevel = 0,
    duration = 0,
    isInitializing = false,
    isTranscribing = false,
    partialTranscript = '',
    noSpeechError = null,
    onCancel,
    onSend,
    onRetry
}) => {
    const barsRef = useRef(Array(BAR_COUNT).fill(0));
    const containerRef = useRef(null);
    const transcriptRef = useRef(null);

    const formatDuration = (seconds) => {
        const m = Math.floor(seconds / 60);
        const s = seconds % 60;
        return `${m}:${s.toString().padStart(2, '0')}`;
    };

    useEffect(() => {
        if (isInitializing || isTranscribing) return;
        barsRef.current = [...barsRef.current.slice(1), audioLevel];
        if (containerRef.current) {
            const barElements = containerRef.current.children;
            for (let i = 0; i < barElements.length; i++) {
                const level = barsRef.current[i];
                const height = MIN_BAR_HEIGHT + level * (MAX_BAR_HEIGHT - MIN_BAR_HEIGHT);
                const hasSound = level > 0.05;
                barElements[i].style.height = `${height}px`;
                barElements[i].style.backgroundColor = hasSound ? '#1f2937' : '#d1d5db';
            }
        }
    }, [audioLevel, isInitializing, isTranscribing]);

    useEffect(() => {
        if (transcriptRef.current && partialTranscript) {
            transcriptRef.current.scrollTop = transcriptRef.current.scrollHeight;
        }
    }, [partialTranscript]);

    if (isInitializing) {
        return (
            <div className="flex items-center gap-3 py-1">
                <div className="flex-1 flex items-center justify-center text-gray-500">
                    <span className="text-sm">Đang kết nối...</span>
                </div>
                <button
                    onClick={onCancel}
                    className="w-7 h-7 flex items-center justify-center rounded-full text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-colors flex-shrink-0"
                    aria-label="Hủy"
                >
                    <X className="w-4 h-4" />
                </button>
            </div>
        );
    }

    if (isTranscribing) {
        return (
            <div className="flex items-center gap-3 py-1">
                <div className="flex-1 flex items-center justify-center gap-2 text-gray-500">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span className="text-sm">Đang nhận diện...</span>
                </div>
            </div>
        );
    }

    if (noSpeechError) {
        return (
            <div className="flex items-center gap-3 py-1">
                <div className="flex-1 flex items-center gap-2 text-amber-600 px-2">
                    <AlertCircle className="w-4 h-4 flex-shrink-0" />
                    <span className="text-sm">{noSpeechError}</span>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                    <button
                        onClick={onCancel}
                        className="w-7 h-7 flex items-center justify-center rounded-full text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-colors"
                        aria-label="Đóng"
                    >
                        <X className="w-4 h-4" />
                    </button>
                    <button
                        onClick={onRetry}
                        className="w-7 h-7 flex items-center justify-center rounded-full border-2 border-brand-blue-500 text-brand-blue-500 hover:bg-brand-blue-500 hover:text-white transition-colors"
                        aria-label="Thử lại"
                    >
                        <RotateCcw className="w-3.5 h-3.5" />
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="flex flex-col">
            <div
                ref={transcriptRef}
                className="text-sm text-gray-700 text-left px-2 min-h-[24px] max-h-[60px] overflow-y-auto flex items-center"
            >
                {partialTranscript ? (
                    <>
                        <span>{partialTranscript}</span>
                        <span className="inline-block w-0.5 h-4 bg-gray-400 ml-0.5 animate-pulse align-middle" />
                    </>
                ) : (
                    <span className="text-gray-400 text-xs">Đang lắng nghe...</span>
                )}
            </div>

            <div className="flex items-center gap-3">
                <span className="text-xs text-gray-400 font-mono w-10 text-center flex-shrink-0">
                    {formatDuration(duration)}
                </span>

                <div
                    ref={containerRef}
                    className="flex-1 flex items-center justify-between overflow-hidden mx-2"
                >
                    {Array.from({ length: BAR_COUNT }, (_, i) => (
                        <div
                            key={i}
                            className="w-[1px] min-w-[1px]"
                            style={{ height: `${MIN_BAR_HEIGHT}px`, backgroundColor: '#d1d5db' }}
                        />
                    ))}
                </div>

                <div className="flex items-center gap-2 flex-shrink-0">
                    <button
                        onClick={onCancel}
                        className="w-7 h-7 flex items-center justify-center rounded-full text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-colors"
                        aria-label="Hủy ghi âm"
                    >
                        <X className="w-4 h-4" />
                    </button>
                    <button
                        onClick={onSend}
                        className="w-7 h-7 flex items-center justify-center rounded-full border-2 border-brand-blue-500 text-brand-blue-500 hover:bg-brand-blue-500 hover:text-white transition-colors"
                        aria-label="Gửi ghi âm"
                    >
                        <Check className="w-3.5 h-3.5" />
                    </button>
                </div>
            </div>
        </div>
    );
};

export default VoiceInputRecording;
